import {
  isAgencyGroupNode,
  isAgencyNode,
  type AgencyGroupNode,
  type AgencyNode,
  type AgencyStructureNode
} from '@gat-ship/types'

/**
 * The root agency group of the tree, or `undefined` when the structure has no
 * root node. Pure selector over the flat agency-structure list.
 */
export function findRootGroup(
  nodes: readonly AgencyStructureNode[]
): AgencyGroupNode | undefined {
  return nodes.find(
    (node): node is AgencyGroupNode =>
      isAgencyGroupNode(node) && node.isRoot === true
  )
}

/** Every inactive agency node (agency groups are excluded). */
export function filterInactiveAgencies(
  nodes: readonly AgencyStructureNode[]
): AgencyNode[] {
  return nodes.filter(
    (node): node is AgencyNode => isAgencyNode(node) && node.isInactive
  )
}

/** The nodes whose direct parent group is `groupId`, in input order. */
export function childrenOfGroup(
  nodes: readonly AgencyStructureNode[],
  groupId: string
): AgencyStructureNode[] {
  return nodes.filter((node) => node.parentGroup?.id === groupId)
}

/**
 * A fresh array sorted by `$type` then `name` (ordinal comparison). Does not
 * mutate the input — sorts a copy, keeping the cached structure untouched.
 */
export function sortNodesByTypeThenName(
  nodes: readonly AgencyStructureNode[]
): AgencyStructureNode[] {
  return [...nodes].sort((a, b) => {
    if (a.$type > b.$type) {
      return 1
    }
    if (a.$type < b.$type) {
      return -1
    }
    // If $type is equal, sort by name
    if (a.name > b.name) {
      return 1
    }
    if (a.name < b.name) {
      return -1
    }
    return 0
  })
}

/**
 * Splits a group's children into its active agencies and active sub-groups,
 * preserving input order. Inactive nodes are dropped from both partitions.
 */
export function partitionActiveChildren(
  children: readonly AgencyStructureNode[]
): { agencies: AgencyNode[]; agencyGroups: AgencyGroupNode[] } {
  return {
    agencies: children.filter(
      (node): node is AgencyNode =>
        isAgencyNode(node) && node.isInactive === false
    ),
    agencyGroups: children.filter(
      (node): node is AgencyGroupNode =>
        isAgencyGroupNode(node) && node.isInactive === false
    )
  }
}
